import React, { useState, useEffect } from "react"
import { bindActionCreators } from "redux"
import { connect } from "react-redux"
import Title from "../general/title"
import _ from "lodash"
import ImageExport from "components/ImageExport"
import classNames from "classnames"
import submitData from "helpers/asyncSubmitData"
import currentUser from "helpers/currentUser"
const RealtorSalesPercentGauge = React.lazy(() => import("widgets/realtorSalesPercentGauge"))
const CancelRateGauge = React.lazy(() => import("widgets/cancelRateGauge"))
const SalesMeter = React.lazy(() => import("widgets/salesMeter"))
const PieSalesByLotType = React.lazy(() => import("widgets/pieSalesByLotType"))
const RealtorInvolved = React.lazy(() => import("widgets/realtorInvolved"))
const DirtVSpec = React.lazy(() => import("widgets/dirtVSpec"))
const BuilderSalesStackedBars = React.lazy(() => import("widgets/builderSalesStackedBars"))
const TopSellingFloorplans = React.lazy(() => import("widgets/topSellingFloorplans"))
const SalesBySalespeople = React.lazy(() => import("widgets/salesBySalespeople"))
const TrafficSources = React.lazy(() => import("widgets/trafficSources"))
const BuilderAveragesTable = React.lazy(() => import("widgets/builderAveragesTable"))
const BuilderSalesStats = React.lazy(() => import("widgets/builderSalesStats"))
const YearOverYearComparison = React.lazy(() => import("widgets/yearOverYearComparison"))
const MonthlyYearOverYearComparison = React.lazy(() => import("widgets/monthlyYearOverYearComparison"))
const OverviewReport = React.lazy(() => import("widgets/overviewReport"))
const TopZipCodes = React.lazy(() => import("widgets/topZipCodes"))
const InventoryStatusPie = React.lazy(() => import("widgets/inventoryStatusPie"))
const SalesOpportunitiesPie = React.lazy(() => import("widgets/salesOpportunitiesPie"))
const VacantLotInventory = React.lazy(() => import("widgets/vacantLotInventory"))
const WeeklyCumulativeSalesChart = React.lazy(() => import("widgets/weeklyCumulativeSalesChart"))
const InventorySelloutSectionLotTypeBuilder = React.lazy(() => import("widgets/inventorySelloutSectionLotTypeBuilder"))
const InventorySelloutLotTypeBuilder = React.lazy(() => import("widgets/inventorySelloutLotTypeBuilder"))
const InventoryLotTypeHealth = React.lazy(() => import("widgets/inventoryLotTypeHealth"))
const InventorySectionHealth = React.lazy(() => import("widgets/inventorySectionHealth"))
const InventorySectionLotTypeHealth = React.lazy(() => import("widgets/inventorySectionLotTypeHealth"))
const DemographicsZipCode = React.lazy(() => import("widgets/demoZipCodes"))
const DemographicsAge = React.lazy(() => import("widgets/demoAge"))
const CumulativeWeeklyTraffic = React.lazy(() => import("widgets/cumulativeWeeklyTraffic"))
const SalesByLotTypeBar = React.lazy(() => import("widgets/salesByLotTypeBar"))
const LotInventoryReplacementTable = React.lazy(() => import("widgets/lotInventoryReplacementTable"))
const Segmentation = React.lazy(() => import("containers/reports/segmentation"))
const FloorplanClosingAnalysis = React.lazy(() => import("widgets/floorplanClosingAnalysis"))
import {
	QuickGrossSales,
	QuickNet,
	QuickCancels,
	QuickTransfers,
	QuickClosings,
	QuickStarts,
	QuickCompletes,
	QuickTraffic,
	QuickBuilderTraffic,
	QuickWelcomeTraffic,
} from "widgets/quick"

function Dashboard(props) {
	// Dynamic dashboard structure
	let defaultDashboard = {
		quickStats: [
			"QuickGrossSales",
			"QuickNet",
			"QuickCancels",
			"QuickTransfers",
			"QuickClosings",
			"QuickStarts",
			"QuickCompletes",
			"QuickTraffic",
		],
		rows: [
			{
				id: "row-1",
				type: "third",
				widgets: ["RealtorSalesPercentGauge", "CancelRateGauge", "DemographicsAge"],
			},
			{
				id: "row-2",
				type: "third",
				widgets: ["PieSalesByLotType", "DirtVSpec", "RealtorInvolved"],
			},
			{
				id: "row-3",
				type: "half",
				widgets: ["BuilderSalesStackedBars", "TopSellingFloorplans"],
			},
			{
				id: "row-4",
				type: "half",
				widgets: ["SalesBySalespeople", "TrafficSources"],
			},
			{
				id: "row-5",
				type: "full",
				widgets: ["SalesMeter"],
			},
			{
				id: "row-6",
				type: "full",
				widgets: ["BuilderAveragesTable"],
			},
			{
				id: "row-7",
				type: "third",
				widgets: ["InventoryStatusPie", "SalesOpportunitiesPie", "VacantLotInventory"],
			},
		],
	}
	useEffect(() => {
		// Load dashboard structure
		let dashboardData = localStorage.getItem("widgets__dashboard_v2")
		if (dashboardData) {
			try {
				const parsedDashboard = JSON.parse(dashboardData)
				setDashboard(parsedDashboard)
				return
			} catch (e) {
				console.error("Failed to parse dashboard data", e)
			}
		}
		// If no saved data, use default
		setDashboard(_.cloneDeep(defaultDashboard))
	}, [])

	useEffect(() => {
		try {
			let user = currentUser()
			if (user?.id) {
				let lookup = _.find(props.payload?.users, { id: user.id })
				if (lookup && lookup.widgets) {
					// Server has saved dashboard layout, use it instead of localStorage
					setDashboard(lookup.widgets)
				}
			}
		} catch (e) {
			console.log(e)
		}
	}, [props.payloadStatus])

	const [editing, setEditing] = useState(false)
	const [widgetTray, setWidgetTray] = useState(false)
	const [editPosition, setEditPosition] = useState("")
	
	// Debug editPosition changes
	useEffect(() => {
	}, [editPosition])
	const [dashboard, setDashboard] = useState(_.cloneDeep(defaultDashboard))
	const [editingType, setEditingType] = useState(false) // this is to distinguish between special cases, like quick stats. Obviously we can't put tables in that area.  false means it's a normal widget, "quick" means it's a quick stat.  Can add to in the future.

	// Debug dashboard changes
	useEffect(() => {
	}, [dashboard])

	// Functions for dynamic dashboard management
	const addRow = (type = "full", insertIndex = null) => {
		const newRowId = `row-${Date.now()}`
		const maxWidgets = type === "full" ? 1 : type === "half" ? 2 : 3
		const newRow = {
			id: newRowId,
			type,
			widgets: Array(maxWidgets).fill(""),
		}

		setDashboard((prev) => {
			const newRows = [...prev.rows]
			if (insertIndex !== null) {
				newRows.splice(insertIndex, 0, newRow)
			} else {
				newRows.push(newRow)
			}
			return {
				...prev,
				rows: newRows,
			}
		})
	}

	const removeRow = (rowId) => {
		setDashboard((prev) => ({
			...prev,
			rows: prev.rows.filter((row) => row.id !== rowId),
		}))
	}

	const updateRowType = (rowId, newType) => {
		const maxWidgets = newType === "full" ? 1 : newType === "half" ? 2 : 3

		setDashboard((prev) => ({
			...prev,
			rows: prev.rows.map((row) => {
				if (row.id === rowId) {
					const newWidgets = [...row.widgets]
					// Adjust widgets array length properly
					if (newWidgets.length > maxWidgets) {
						// If shrinking, keep only the first widgets
						newWidgets.length = maxWidgets
					} else if (newWidgets.length < maxWidgets) {
						// If expanding, add empty slots
						const emptySlots = maxWidgets - newWidgets.length
						newWidgets.push(...Array(emptySlots).fill(""))
					}
					return { ...row, type: newType, widgets: newWidgets }
				}
				return row
			}),
		}))
	}

	const updateWidget = (rowId, widgetIndex, componentName) => {
		
		setDashboard((prev) => {
			
			// Handle quickStats separately
			if (rowId === "quickStats") {
				const newQuickStats = [...prev.quickStats]
				newQuickStats[widgetIndex] = componentName
				const updatedDashboard = {
					...prev,
					quickStats: newQuickStats
				}
				return updatedDashboard
			}
			
			// Find the specific row
			const rowIndex = prev.rows.findIndex(row => row.id === rowId)
			if (rowIndex === -1) {
				console.error("Row not found:", rowId)
				console.error("Available rows:", prev.rows.map(r => ({ id: r.id, type: r.type })))
				return prev
			}
			
			// Create new dashboard with updated widget
			const updatedDashboard = {
				...prev,
				rows: prev.rows.map((row, index) => {
					if (index === rowIndex) {
						const newWidgets = [...row.widgets]
						newWidgets[widgetIndex] = componentName
						return {
							...row,
							widgets: newWidgets
						}
					}
					return row
				})
			}
			
			
			return updatedDashboard
		})
		
		// Close the widget tray immediately without delay
		setWidgetTray(false)
		setEditPosition("")
	}

	// Need to load all these modules
	let components = {
		QuickGrossSales,
		QuickNet,
		QuickCancels,
		QuickTransfers,
		QuickClosings,
		QuickStarts,
		QuickCompletes,
		QuickTraffic,
		QuickBuilderTraffic,
		QuickWelcomeTraffic,
		CumulativeWeeklyTraffic,
		LotInventoryReplacementTable,
		FloorplanClosingAnalysis,
		SalesByLotTypeBar,
		Segmentation,
		RealtorSalesPercentGauge,
		CancelRateGauge,
		SalesMeter,
		PieSalesByLotType,
		RealtorInvolved,
		DirtVSpec,
		BuilderSalesStackedBars,
		TopSellingFloorplans,
		SalesBySalespeople,
		TrafficSources,
		BuilderAveragesTable,
		BuilderSalesStats,
		YearOverYearComparison,
		MonthlyYearOverYearComparison,
		OverviewReport,
		TopZipCodes,
		InventoryStatusPie,
		SalesOpportunitiesPie,
		VacantLotInventory,
		WeeklyCumulativeSalesChart,
		DemographicsAge,
		InventorySelloutLotTypeBuilder,
		InventorySelloutSectionLotTypeBuilder,
		InventoryLotTypeHealth,
		InventorySectionHealth,
		InventorySectionLotTypeHealth,
		DemographicsZipCode,
	}

	let widgets = [
		{
			name: "Quick Gross Sales",
			component: "QuickGrossSales",
			quick: true,
		},
		{
			name: "Quick Net Sales",
			component: "QuickNet",
			quick: true,
		},
		{
			name: "Quick Cancels",
			component: "QuickCancels",
			quick: true,
		},
		{
			name: "Quick Transfers",
			component: "QuickTransfers",
			quick: true,
		},
		{
			name: "Quick Closings",
			component: "QuickClosings",
			quick: true,
		},
		{
			name: "Quick Starts",
			component: "QuickStarts",
			quick: true,
		},
		{
			name: "Quick Traffic",
			component: "QuickTraffic",
			quick: true,
		},
		{
			name: "Quick Builder Traffic",
			component: "QuickBuilderTraffic",
			quick: true,
		},
		{
			name: "Quick Welcome Traffic",
			component: "QuickWelcomeTraffic",
			quick: true,
		},
		{
			name: "Quick Completes",
			component: "QuickCompletes",
			quick: true,
		},
		{
			name: "Realtor Sales Percent Gauge",
			component: "RealtorSalesPercentGauge",
		},
		{
			name: "Cumulative Weekly Traffic",
			component: "CumulativeWeeklyTraffic",
		},
		{
			name: "Cancel Rate Gauge",
			component: "CancelRateGauge",
		},
		{
			name: "Sales Meter",
			component: "SalesMeter",
		},
		{
			name: "Pie Sales By Lot Type",
			component: "PieSalesByLotType",
		},
		{
			name: "Realtor Involved",
			component: "RealtorInvolved",
		},
		{
			name: "Dirt vs Spec",
			component: "DirtVSpec",
		},
		{
			name: "Builder Sales Stacked Bars",
			component: "BuilderSalesStackedBars",
		},
		{
			name: "Top Selling Floorplans",
			component: "TopSellingFloorplans",
		},
		{
			name: "Sales By Salespeople",
			component: "SalesBySalespeople",
		},
		{
			name: "Traffic Sources",
			component: "TrafficSources",
		},
		{
			name: "Builder Averages Table",
			component: "BuilderAveragesTable",
		},
		{
			name: "Builder Sales Stats",
			component: "BuilderSalesStats",
		},
		{
			name: "Year-over-Year Comparison",
			component: "YearOverYearComparison",
		},
		{
			name: "Monthly Year Over Year Comparison",
			component: "MonthlyYearOverYearComparison",
		},
		{
			name: "Overview Report",
			component: "OverviewReport",
		},
		{
			name: "Top Zip Codes",
			component: "TopZipCodes",
		},
		{
			name: "Inventory Status Pie",
			component: "InventoryStatusPie",
		},
		{
			name: "Sales Opportunities Pie",
			component: "SalesOpportunitiesPie",
		},
		{
			name: "Vacant Lot Inventory",
			component: "VacantLotInventory",
		},
		{
			name: "Weekly Cumulative Sales Chart",
			component: "WeeklyCumulativeSalesChart",
		},
		{
			name: "Demographics Age",
			component: "DemographicsAge",
		},
		{
			name: "Inventory Sellout Section Lot Type Builder",
			component: "InventorySelloutSectionLotTypeBuilder",
		},
		{
			name: "Inventory Sellout Lot Type Builder",
			component: "InventorySelloutLotTypeBuilder",
		},
		{
			name: "Inventory Lot Type Health",
			component: "InventoryLotTypeHealth",
		},
		{
			name: "Inventory Section Health",
			component: "InventorySectionHealth",
		},
		{
			name: "Inventory Section, Lot Type Health",
			component: "InventorySectionLotTypeHealth",
		},
		{
			name: "Demographics Zip Code",
			component: "DemographicsZipCode",
		},
		{
			name: "Sales By Lot Type Bar",
			component: "SalesByLotTypeBar",
		},
		{
			name: "Lot Inventory Replacement Table",
			component: "LotInventoryReplacementTable",
		},
		{
			name: "Segmentation",
			component: "Segmentation",
		},
		{
			name: "Floorplan Closing Analysis",
			component: "FloorplanClosingAnalysis",
		},
	]

	const OutputWidget = (v) => {
		let exp = v.pos.split("-")
		let name = widgetOrder[exp[0]][exp[1]]
		if (!components[name]) {
			// Eitehr the component doesn't exist or it's not in the list of widgets, add from default
			console.warn(`Cannot find widget: ${name}`)
			return null
		}
		const Component = components[name]
		return (
			<>
				<div className="statBox__widgetControls">
					{editing ? (
						<i
							className={classNames(`fa-solid fa-file-pen lightShake`, {
								"mr-0 !text-white": v.export === false,
							})}
							onClick={() => {
								setWidgetTray(true)
								setEditPosition(v.pos)
								if (v.quick) setEditingType("quick")
								else setEditingType(false)
							}}></i>
					) : null}
					{v.export === false ? null : <ImageExport name={name} twoUp={true} />}
				</div>
				<Component {...props} widget={true} />
			</>
		)
	}

	const widgetsList = () => {
		let list = []
		for (let v of widgets) {
			if (editingType === "quick" && !v.quick) continue
			if (editingType === false && v.quick) continue
			list.push(
				<li
					className="cursor-pointer border border-dashed border-gray-400 rounded-lg px-2 py-1 mr-2 mb-1"
					key={`widget-${v.component}`}
					onClick={() => {
						
						if (editPosition && editPosition.includes("-")) {
							
							// Handle quickStats differently than regular rows
							if (editPosition.startsWith("quickStats-")) {
								const widgetIndex = editPosition.split("-")[1]
								updateWidget("quickStats", parseInt(widgetIndex), v.component)
							} else {
								// For regular rows, find the last dash to separate rowId from widgetIndex
								const lastDashIndex = editPosition.lastIndexOf("-")
								const rowId = editPosition.substring(0, lastDashIndex)
								const widgetIndex = editPosition.substring(lastDashIndex + 1)
								updateWidget(rowId, parseInt(widgetIndex), v.component)
							}
						} else {
							console.error("Invalid edit position:", editPosition)
							console.error("Edit position does not contain '-' or is empty")
						}
					}}>
					{v.name}
				</li>
			)
		}

		return list
	}

	// Dynamic dashboard rendering functions
	const renderDynamicWidget = (rowId, widgetIndex, componentName) => {
		// If no component is selected, show empty placeholder
		if (!componentName || componentName === "") {
			return (
				<div 
					className={`min-h-[200px] border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center cursor-pointer hover:bg-gray-50 ${editing ? 'bg-gray-100' : 'bg-transparent'}`}
					onClick={() => {
						if (editing) {
							const position = `${rowId}-${widgetIndex}`
							setEditPosition(position)
							setEditingType(false)
							setWidgetTray(true)
						}
					}}
				>
					{editing ? (
						<div className="text-center text-gray-500">
							<i className="fa-solid fa-plus text-2xl mb-2 block"></i>
							<div className="text-sm font-medium">Click to add widget</div>
						</div>
					) : (
						<div className="text-center text-gray-400">
							<div className="text-sm">Empty widget slot</div>
						</div>
					)}
				</div>
			)
		}

		// If component doesn't exist, show error
		if (!components[componentName]) {
			return (
				<div className="min-h-[200px] border-2 border-dashed border-red-300 rounded-lg flex items-center justify-center bg-red-50">
					<div className="text-center text-red-500">
						<i className="fa-solid fa-exclamation-triangle text-2xl mb-2 block"></i>
						<div className="text-sm font-medium">Widget not found: {componentName}</div>
					</div>
				</div>
			)
		}

		// Render the actual component
		const Component = components[componentName]
		return (
			<React.Suspense fallback={<div className="min-h-[200px] flex items-center justify-center"><div className="text-gray-400">Loading...</div></div>}>
				{/* Only show widget controls for non-quickStats widgets */}
				{rowId !== "quickStats" && (
					<div className="statBox__widgetControls">
						{editing ? (
							<i
								className="fa-solid fa-file-pen lightShake mr-2 cursor-pointer"
								onClick={() => {
									setWidgetTray(true)
									setEditPosition(`${rowId}-${widgetIndex}`)
									setEditingType(false)
								}}></i>
						) : null}
						<ImageExport name={componentName} twoUp={true} />
					</div>
				)}
				<Component {...props} widget={true} />
			</React.Suspense>
		)
	}

	const renderDynamicRow = (row) => {
		const getRowClass = () => {
			switch (row.type) {
				case "full":
					return "statBox__row"
				case "half":
					return "statBox__row statBox__row--double"
				case "third":
					return "statBox__row statBox__row--triple"
				default:
					return "statBox__row"
			}
		}

		return (
			<div key={row.id} className="relative">
				{editing && (
					<div className="absolute top-0 right-2 z-10 flex gap-2">
						<select
							value={row.type}
							onChange={(e) => updateRowType(row.id, e.target.value)}
							className="text-xs px-2 py-1 border rounded">
							<option value="full">Full</option>
							<option value="half">Half</option>
							<option value="third">Third</option>
						</select>
						<button
							onClick={() => removeRow(row.id)}
							className="text-red-500 hover:text-red-700 text-xs px-2 py-1 border rounded">
							×
						</button>
					</div>
				)}
				<div className={getRowClass()}>
					{row.widgets.map((widget, index) => (
						<div key={`${row.id}-${index}`} className="statBox__box">
							{renderDynamicWidget(row.id, index, widget)}
						</div>
					))}
				</div>
			</div>
		)
	}

	const renderAddRowButtons = (insertIndex = null) => {
		if (!editing) return null

		return (
			<div className="mb-2 p-2 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
				<div className="flex gap-2 justify-center">
					<button
						onClick={() => addRow("full", insertIndex)}
						className="px-3 py-2 bg-blue-500 text-blue text-sm font-medium rounded hover:bg-blue-600">
						Add Full Width
					</button>
					<button
						onClick={() => addRow("half", insertIndex)}
						className="px-3 py-2 bg-blue-500 text-blue text-sm font-medium rounded hover:bg-blue-600">
						Add Half (2 cols)
					</button>
					<button
						onClick={() => addRow("third", insertIndex)}
						className="px-3 py-2 bg-blue-500 text-blue text-sm font-medium rounded hover:bg-blue-600">
						Add Third (3 cols)
					</button>
				</div>
			</div>
		)
	}

	if (props.payloadStatus) {
		return (
			<div>
				<div className="imageGroup">
					<div className="flex justify-end">
						{editing ? (
							<div
								className="text-red-500 text-xs mr-3 mt-1 font-bold cursor-pointer"
								onClick={() => {
									setDashboard(_.cloneDeep(defaultDashboard))
									localStorage.removeItem("widgets__dashboard_v2")
									setEditing(false)
								}}>
								Reset Dashboard
							</div>
						) : null}
						<div
							className="px-4 rounded-bl-lg bg-slate-600 rounded-br-lg py-1 text-white text-xs cursor-pointer transition duration-300 hover:bg-slate-500 mb-3"
							onClick={() => {
								if (editing) {
									// Save dashboard structure
									localStorage.setItem("widgets__dashboard_v2", JSON.stringify(dashboard))

									// Save to server
									submitData("mainIngest", {
										action: "storeWidgets",
										widgets: dashboard,
									})
										.then(() => {
											console.log("Dashboard saved to server successfully")
										})
										.catch((error) => {
											console.error("Failed to save dashboard to server:", error)
										})
								}
								setEditing(!editing)
							}}>
							{editing ? "Save" : "Edit"} Widgets
						</div>
					</div>
					<Title title="Dashboard" compare={false} />

					{/* Quick Stats */}
					<div className="quickStats">
						<ul className="quickStats__list">
							{dashboard.quickStats.map((componentName, index) => (
								<li key={index} className="quickStats__item">
									{renderDynamicWidget("quickStats", index, componentName)}
								</li>
							))}
						</ul>
					</div>

					{/* Main content area */}
					{editing && renderAddRowButtons(0)}
					<div className="statBox">
						{dashboard.rows.map((row, index) => (
							<div key={row.id}>
								{renderDynamicRow(row)}
								{editing && renderAddRowButtons(index + 1)}
							</div>
						))}
					</div>
				</div>
				<div className="imageGroup">
					<div className="footnotes">
						<h3>Footnotes</h3>
						<ol className="text-sm">
							<li>Delivered: Sections which are substantially complete.</li>
							<li>
								Pending: Sections which are not yet substantially complete, but are in lot purchase
								contracts to builders and are pre-sale opportunities.
							</li>
						</ol>
					</div>
				</div>
				<div
					className={classNames(`widgetTray`, {
						active: widgetTray,
					})}>
					<i
						className="fa-solid fa-circle-xmark cursor-pointer absolute right-3 top-3 text-blue text-2xl"
						onClick={() => {
							setWidgetTray(false)
						}}></i>
					<h2 className="pt-0 mb-3">Available Widgets</h2>
					<ul className="flex flex-wrap hover:child:bg-slate-200 child:transition-all child:duration-300">
						{widgetsList()}
					</ul>
				</div>
			</div>
		)
	} else {
		return ""
	}
}

function mapStateToProps(state) {
	return {
		currentDates: state.currentDates,
		compareDates: state.compareDates,
		payload: state.payload,
		payloadStatus: state.payloadStatus,
		activeProperties: state.activeProperties,
		compareStatus: state.compareStatus,
	}
}

function matchDispatchToProps(dispatch) {
	return bindActionCreators({}, dispatch)
}

export default connect(mapStateToProps, matchDispatchToProps)(Dashboard)
